import { useState } from 'react'
import { useAppStore } from '../stores/useAppStore'
import { apiEndpoints, config } from '../config/env'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

interface LoginCredentials {
  username: string
  password: string
}

interface LoginResponse {
  userID?: number
  id?: number
  username: string
  firstName?: string
  lastName?: string
  fullName?: string
  name?: string
  email?: string
  emailAddress?: string
  roles?: string[]
  technicianId?: number
  token?: string
  tokenExpires?: string
}

const LoginForm = () => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: ''
  })
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  
  const { setUser, setToken, addNotification } = useAppStore()

  // Development fallback when API server is not available
  // const developmentLogin = (username: string): LoginResponse => {
  //   console.log('🔧 Using development login fallback')
  //   return {
  //     userID: 1,
  //     username: username,
  //     firstName: username === 'MikeTech' ? 'Mike' : 'Test',
  //     lastName: username === 'MikeTech' ? 'Smith' : 'User',
  //     fullName: username === 'MikeTech' ? 'Mike Smith' : 'Test User',
  //     emailAddress: `${username.toLowerCase()}@coastsmokealarms.com.au`,
  //     roles: username === 'MikeTech' ? ['Technician'] : ['Admin'],
  //     technicianId: username === 'MikeTech' ? 123 : undefined,
  //     token: 'dev-token-' + Date.now(),
  //     tokenExpires: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString() // 8 hours
  //   }
  // }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoggingIn(true)

    try {
      
      const response = await fetch(apiEndpoints.auth.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      })


      let data: LoginResponse

      if (response.ok) {
        data = await response.json()
      } else {
        // API server not available or returned error - use development fallback
        const errorData = await response.json().catch(() => ({ message: 'Login failed' }))
        throw new Error(errorData.message || 'Login failed')
      }
      
        // Handle different possible response structures
       // id: (data.userID || data.id || 1).toString(),
      const userData = {
          id: (data.userID || data.id || 1).toString(),
        name: data.fullName || data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.username || 'User',
        email: data.emailAddress || data.email || `${credentials.username}@coastsmokealarms.com.au`,
        username: data.username || credentials.username,
        roles: data.roles || ['Technician'],
        technicianId: data.technicianId
      }

      
      // Set user and token in store
      setUser(userData)
      
      if (data.token) {
        const tokenExpires = data.tokenExpires ? new Date(data.tokenExpires) : new Date(Date.now() + 8 * 60 * 60 * 1000)
        setToken(data.token, tokenExpires)
      }

      addNotification({
        type: 'success',
        message: `Welcome back, ${userData.name}!`,
        timestamp: Date.now()
      })

    } catch (error) {
      console.error('🔧 Login error:', error)

      addNotification({
        type: 'error',
        message: 'Network error. Please check your connection and try again.',
        timestamp: Date.now()
      })

    } finally {
      setIsLoggingIn(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center space-y-2 pb-6">
            <CardTitle className="text-2xl font-bold text-gray-900">
              Coast Smoke Alarms
            </CardTitle>
            <CardDescription className="text-lg text-gray-600">
              Technician Portal
            </CardDescription>
            {config.isDevelopment && (
              <div className="text-xs text-gray-500 bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-200">
                Development Mode - API: {config.apiBaseUrl}
              </div>
            )}
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                  Username
                </Label>
                <Input
                  type="text"
                  id="username"
                  name="username"
                  value={credentials.username}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your username"
                  disabled={isLoggingIn}
                  className="h-12 text-base"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={credentials.password}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your password"
                    disabled={isLoggingIn}
                    className="h-12 text-base pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
                    disabled={isLoggingIn}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoggingIn}
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-all duration-200"
              >
                {isLoggingIn ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Secure access for authorized technicians only
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default LoginForm 